import { useEffect, useState } from 'react'
import { dripAPI } from '../../services/api'
import type { Subscription, UnlockedDripPiece } from '../../types'
import { displayName } from '../monetization/subscriptionsView'

/**
 * My Subscriptions: what each creator's drip schedule has unlocked for you,
 * newest first. Nothing shows when there's nothing unlocked.
 */
const UnlockedContent = ({ subscriptions }: { subscriptions: Subscription[] }) => {
  const [pieces, setPieces] = useState<(UnlockedDripPiece & { creator: string })[]>([])
  useEffect(() => {
    let stopped = false
    const ids = Array.from(new Set(subscriptions.map((s) => s.creator_id)))
    // Optional: a creator whose pieces can't be loaded is left out.
    Promise.all(
      ids.map((id) =>
        Promise.resolve()
          .then(() => dripAPI.unlocked(id))
          .then((list) => {
            const sub = subscriptions.find((s) => s.creator_id === id)
            const creator = sub?.creator ? displayName(sub.creator) : 'Your creator'
            return list.map((p) => ({ ...p, creator }))
          })
          .catch(() => [])
      )
    ).then((lists) => {
      if (!stopped) setPieces(lists.flat().sort((a, b) => b.delivered_at.localeCompare(a.delivered_at)))
    })
    return () => {
      stopped = true
    }
  }, [subscriptions])

  if (pieces.length === 0) return null

  return (
    <section className="mt-6 rounded-lg bg-white p-4 shadow" aria-label="Unlocked for you">
      <h2 className="text-lg font-semibold text-gray-900">Unlocked for you</h2>
      <ul className="mt-3 space-y-4">
        {pieces.map((p) => (
          <li key={`${p.subscription_id}-${p.dripped_content_id}`}>
            <p className="text-xs text-gray-500">
              {p.creator} · day {p.unlock_day} · {new Date(p.delivered_at).toLocaleDateString()}
            </p>
            <h3 className="font-medium text-gray-900">{p.title}</h3>
            <p className="whitespace-pre-line text-sm text-gray-700">{p.body}</p>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default UnlockedContent
